/**
 * Session Timeout Component
 *
 * Displays a warning dialog when the user's session is about to expire due to inactivity.
 * Shows a countdown timer and provides options to stay logged in or logout.
 *
 * @example
 * ```typescript
 * // In parent component template
 * <app-session-timeout
 *   [visible]="showTimeoutWarning"
 *   [remainingSeconds]="timeRemaining"
 *   (stayLoggedIn)="handleStayLoggedIn()"
 *   (logout)="handleLogout()">
 * </app-session-timeout>
 * ```
 */

import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { interval, Subscription } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-session-timeout',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TranslatePipe
  ],
  templateUrl: './session-timeout.component.html',
  styleUrl: './session-timeout.component.scss'
})
export class SessionTimeoutComponent implements OnChanges, OnDestroy {
  /**
   * Whether the dialog is visible
   */
  @Input() visible: boolean = false;

  /**
   * Remaining seconds until session expires
   */
  @Input() remainingSeconds: number = 0;

  /**
   * Emitted when user clicks "Stay Logged In" button
   */
  @Output() stayLoggedIn = new EventEmitter<void>();

  /**
   * Emitted when user clicks "Logout" button
   */
  @Output() logout = new EventEmitter<void>();

  /**
   * Emitted when dialog is closed
   */
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * Display countdown in MM:SS format
   */
  countdownDisplay: string = '0:00';

  /**
   * Internal countdown in seconds
   */
  private countdown: number = 0;

  /**
   * Subscription for countdown timer
   */
  private countdownSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.startCountdown();
    } else if (changes['visible'] && !this.visible) {
      this.stopCountdown();
    }

    if (changes['remainingSeconds'] && this.remainingSeconds > 0) {
      this.countdown = this.remainingSeconds;
      this.updateDisplay();
    }
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  /**
   * Handles the "Stay Logged In" button click
   *
   * Emits stayLoggedIn event and closes the dialog.
   */
  onStayLoggedIn(): void {
    this.stayLoggedIn.emit();
    this.closeDialog();
  }

  /**
   * Handles the "Logout" button click
   *
   * Emits logout event and closes the dialog.
   */
  onLogout(): void {
    this.logout.emit();
    this.closeDialog();
  }

  /**
   * Closes the dialog
   */
  private closeDialog(): void {
    this.stopCountdown();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Starts the countdown timer
   *
   * Updates the display every second until countdown reaches zero.
   */
  private startCountdown(): void {
    this.stopCountdown();
    this.countdown = this.remainingSeconds;
    this.updateDisplay();

    this.countdownSubscription = interval(1000).subscribe(() => {
      this.countdown--;
      this.updateDisplay();

      if (this.countdown <= 0) {
        this.stopCountdown();
        // Auto-logout when countdown reaches zero
        this.onLogout();
      }
    });
  }

  /**
   * Stops the countdown timer
   */
  private stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
  }

  /**
   * Updates the countdown display in MM:SS format
   */
  private updateDisplay(): void {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    this.countdownDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
